import { useFormik } from "formik"
import * as Yup from "yup"
import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Card,
  CardBody,
  Label,
  Button,
  Form,
  Input,
  FormFeedback,
  Row,
  Col,
} from "reactstrap"
import CustomHeaderTitle from "components/Common/CustomHeaderTitle"
import CustomSelect from "components/Common/SelectBoxes/CustomSelect"
import { isEmpty, isObject } from "lodash"
import { useDispatch, useSelector } from "react-redux"
import Flatpickr from "react-flatpickr"
import moment from "moment"
import Dropzone from "react-dropzone"
import { Link } from "react-router-dom"
import { getProviderInAvailableItems } from "store/provider-in/provider-in-action"
import { getAdjustmentReceipts } from "store/adjustment/adjustment-action"
import ReceiptListForAdjustment from "./ReceiptListForAdjustment"
import { adjustmentAction } from "store/adjustment/adjustment-slice"

const ADJUSTMENT_TYPES = [
  { value: "Increase", label: "Increase" },
  { value: "Decrease", label: "Decrease" },
]

const REASONS_BY_TYPE = {
  Increase: [
    { value: "Found", label: "Found" },
    { value: "Date Correction", label: "Date Correction" },
  ],
  Decrease: [
    { value: "Expired", label: "Expired" },
    { value: "Damaged", label: "Damaged" },
    { value: "Lost", label: "Lost" },
    { value: "Date Correction", label: "Date Correction" },
  ],
}

const AdjustmentForm = ({
  onSubmit,
  loading,
  formMode,
  error,
  categories = [],
  items = [],
  distribution,
}) => {
  const [selectedFiles, setselectedFiles] = useState({})
  const [submited, setSubmited] = useState([])
  const submissionDateRef = useRef(null)
  const [customErrors, setCustomErrors] = useState({})
  const dispatch = useDispatch()
  const receipts = useSelector(state => state.adjustment.receipts)

  //

  function handleAcceptedFiles(files) {
    files.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    )
    setselectedFiles(files[0])
  }

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  useEffect(() => {
    return () => {
      setSubmited([])
      dispatch(adjustmentAction.resetReceipts())
    }
  }, [])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      item_category_id: distribution ? distribution?.item_category_id : "",
      item_id: distribution ? distribution?.item_id : "",
      adjustment_type: distribution
        ? distribution?.adjustment_type
        : ADJUSTMENT_TYPES[0].value,
      reason: distribution
        ? distribution?.reason
        : REASONS_BY_TYPE[ADJUSTMENT_TYPES[0].value][0].value,
      note: distribution ? distribution?.note : "",
      qty: distribution ? distribution?.qty : "",
      // Edit mode: backend returns created_at; keep using date field in the form
      date: distribution ? distribution?.date || "" : "",
    },
    validationSchema: Yup.object().shape({
      date: Yup.string().required("Submission date is required"),
      // qty is required only in Edit mode
      qty:
        formMode === "Edit"
          ? Yup.number()
              .typeError("Qty must be a number")
              .positive("Qty must be greater than 0")
              .required("Qty is required")
          : Yup.mixed().notRequired(),
    }),
    onSubmit: values => {
      if (formMode === "Add") {
        onSubmit(submited, selectedFiles)
      } else {
        const vals = {
          item_category_id: isObject(values.item_category_id)
            ? values.item_category_id.value
            : values.item_category_id,
          item_id: isObject(values.item_id)
            ? values.item_id.value
            : values.item_id,
          adjustment_type: isObject(values.adjustment_type)
            ? values.adjustment_type.value
            : values.adjustment_type,
          reason: isObject(values.reason) ? values.reason.value : values.reason,
          note: values.note,
          qty: values.qty,
          date: values.date,
        }
        onSubmit(vals, selectedFiles)
      }
    },
  })

  const onCategoryChange = value => {
    formik.setFieldValue("item_category_id", value)
    dispatch(getProviderInAvailableItems({ item_category_id: value.value }))
    formik.setFieldValue("item_id", null)
  }

  const onItemChange = value => {
    formik.setFieldValue("item_id", value)
  }

  const onSubmitionDateChange = value => {
    formik.setFieldValue("date", moment(value[0]).format("YYYY-MM-DD"))
  }

  const onSubmitAddHandler = value => {
    if (isEmpty(formik.values.date)) {
      setCustomErrors(prev => ({
        ...prev,
        date: "Submission date is required",
      }))
      return
    }
    setCustomErrors(prev => ({ ...prev, date: null }))
    setSubmited(prev => [
      ...prev,
      {
        item_category_id: isObject(formik.values.item_category_id)
          ? formik.values.item_category_id.value
          : formik.values.item_category_id,
        item_id: isObject(formik.values.item_id)
          ? formik.values.item_id.value
          : formik.values.item_id,
        provider_in_id: value.provider_in_id,
        qty: value.qty,
        adjustment_type: isObject(formik.values.adjustment_type)
          ? formik.values.adjustment_type.value
          : formik.values.adjustment_type,
        reason: isObject(formik.values.reason)
          ? formik.values.reason.value
          : formik.values.reason,
        note: formik.values.note,
        date: formik.values.date,
        batch_number: value.batch_number,
      },
    ])
  }

  const filteredItems = useMemo(
    () =>
      items.map(item => ({
        value: item.id,
        label: `${item.unfp_item_name} ( ${item.item_name} )`,
      })),
    [items]
  )

  // Get filtered reasons based on adjustment type
  const filteredReasons = useMemo(() => {
    const adjustmentType = isObject(formik.values.adjustment_type)
      ? formik.values.adjustment_type.value
      : formik.values.adjustment_type
    return REASONS_BY_TYPE[adjustmentType] || REASONS_BY_TYPE.Decrease
  }, [formik.values.adjustment_type])

  // Handle adjustment type change
  const onAdjustmentTypeChange = val => {
    formik.setFieldValue("adjustment_type", val)
    // Reset reason to first option of the new type
    const newType = isObject(val) ? val.value : val
    const newReasons = REASONS_BY_TYPE[newType] || REASONS_BY_TYPE.Decrease
    formik.setFieldValue("reason", newReasons[0])
    const date = formik.values.date
    const item = formik.values.item_id
    dispatch(
      getAdjustmentReceipts({
        itemId: item.value,
        submitionDate: date,
        adjustmentType: newType,
      })
    )
  }

  return (
    <Card>
      <CardBody>
        <CustomHeaderTitle
          loading={loading}
          title={formMode === "Edit" ? "Edit Adjustment" : "Add Adjustment"}
          lgColumn={4}
          rowCount={false}
          orderingList={false}
          searchBox={false}
          rightBtn={true}
          rightBtnTitle={"Back"}
          rightBtnRoute={"/provider-adjustments"}
        />

        <Form
          onSubmit={e => {
            e.preventDefault()
            formik.handleSubmit()
            return false
          }}
        >
          <Dropzone
            onDrop={acceptedFiles => {
              handleAcceptedFiles(acceptedFiles)
            }}
            useFsAccessApi={false}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <div className="dropzone">
                <div className="dz-message needsclick mt-2" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className="mb-3">
                    <i className="display-4 text-muted bx bxs-cloud-upload" />
                  </div>
                  <h4>Drop files here or click to upload.</h4>
                </div>
              </div>
            )}
          </Dropzone>
          <div className="dropzone-previews mt-3" id="file-previews">
            <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete file">
              <div className="p-2">
                <Row className="align-items-center">
                  <Col>
                    <Link to="#" className="text-muted font-weight-bold">
                      {selectedFiles.name}
                    </Link>
                    <p className="mb-0">
                      <strong>{selectedFiles.formattedSize}</strong>
                    </p>
                  </Col>
                </Row>
              </div>
            </Card>
            {error?.errors?.document && (
              <p className="text-danger p-2">{error?.errors?.document[0]}</p>
            )}
          </div>

          <div className="mb-3">
            <Row>
              <Col>
                <Label className="form-label">Item Categories</Label>
                {categories && (
                  <CustomSelect
                    name="item_category_id"
                    options={categories}
                    isDisabled={formMode === "Edit"}
                    value={
                      isObject(formik.values.item_category_id)
                        ? formik.values.item_category_id
                        : categories.find(
                            c => formik.values.item_category_id === c.value
                          ) || null
                    }
                    onChange={onCategoryChange}
                  />
                )}
              </Col>
              <Col>
                <Label className="form-label">Items</Label>
                {filteredItems && (
                  <CustomSelect
                    name="item_id"
                    options={filteredItems}
                    isDisabled={formMode === "Edit"}
                    value={
                      isObject(formik.values.item_id)
                        ? formik.values.item_id
                        : filteredItems.find(
                            i => formik.values.item_id === i.value
                          ) || null
                    }
                    onChange={onItemChange}
                  />
                )}
              </Col>
            </Row>
          </div>

          <div className="mb-3">
            <Row>
              <Col>
                <Label className="form-label">Adjustment Type</Label>
                <CustomSelect
                  name="adjustment_type"
                  options={ADJUSTMENT_TYPES}
                  value={
                    isObject(formik.values.adjustment_type)
                      ? formik.values.adjustment_type
                      : ADJUSTMENT_TYPES.find(
                          o => o.value === formik.values.adjustment_type
                        )
                  }
                  onChange={onAdjustmentTypeChange}
                />
              </Col>
              <Col>
                <Label className="form-label">Reason</Label>
                <CustomSelect
                  name="reason"
                  options={filteredReasons}
                  value={
                    isObject(formik.values.reason)
                      ? formik.values.reason
                      : filteredReasons.find(
                          o => o.value === formik.values.reason
                        )
                  }
                  onChange={val => formik.setFieldValue("reason", val)}
                />
              </Col>
            </Row>
          </div>

          {formMode === "Edit" && (
            <div className="mb-3">
              <Label className="form-label">Qty</Label>
              <Input
                name="qty"
                placeholder="Quantity"
                type="number"
                min={1}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.qty}
                invalid={
                  (formik.touched.qty && formik.errors.qty) || error?.qty?.[0]
                    ? true
                    : false
                }
              />
              {(formik.touched.qty && formik.errors.qty) || error?.qty?.[0] ? (
                <FormFeedback type="invalid">
                  {formik.errors.qty || error?.qty?.[0]}
                </FormFeedback>
              ) : null}
            </div>
          )}

          <div className="mb-3">
            <Label className="form-label">Note</Label>
            <Input
              name="note"
              placeholder="Optional note"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.note || ""}
              invalid={
                (formik.touched.note && formik.errors.note) || error?.note?.[0]
                  ? true
                  : false
              }
            />
            {(formik.touched.note && formik.errors.note) || error?.note?.[0] ? (
              <FormFeedback type="invalid">
                {formik.errors.note || error?.note?.[0]}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label className="form-label">Submission Date</Label>
            <Flatpickr
              ref={submissionDateRef}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              value={formik.values.date}
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
                defaultDate: formik.values.date,
                maxDate: moment().format("YYYY-MM-DD"),
              }}
              onChange={onSubmitionDateChange}
            />
            {(formik.touched.date && formik.errors.date) || error?.date?.[0] ? (
              <FormFeedback type="invalid">
                {formik.errors.date || error?.date?.[0]}
              </FormFeedback>
            ) : null}
          </div>

          {customErrors &&
            Object.values(customErrors).map((err, key) => (
              <p className="text-danger" key={key}>
                {err}
              </p>
            ))}

          {receipts?.data && formMode === "Add" && (
            <ReceiptListForAdjustment
              data={receipts?.data}
              onSubmitAddHandler={onSubmitAddHandler}
              submited={submited}
              adjustmentType={
                isObject(formik.values.adjustment_type)
                  ? formik.values.adjustment_type.value
                  : formik.values.adjustment_type
              }
            />
          )}

          {submited.length > 0 && (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Qty</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {submited.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.batch_number}</td>
                      <td>{r.qty}</td>
                      <td>
                        <Button
                          size="sm"
                          color="danger"
                          onClick={() =>
                            setSubmited(prev =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="d-flex flex-wrap gap-2">
            <Button type="submit" color="primary" disabled={loading}>
              {loading
                ? "Please Wait..."
                : formMode === "Add"
                ? "Save All"
                : "Update"}
            </Button>
          </div>
        </Form>
        <br />
      </CardBody>
    </Card>
  )
}

export default AdjustmentForm
